export interface MastraAgentRef {
    /** Key used to register and route to the agent on the Mastra server. */
    readonly agentKey: string;
    /** Stable agent identifier for memory, threads, and persisted metadata. */
    readonly id: string;
    /** Human-readable name for the agent instance and admin UI. */
    readonly name: string;
}

export const CONVERSATIONAL_MM_AGENT = {
    agentKey: 'ConversationalMMAgent',
    id: 'conversational-mm-agent',
    name: 'ConversationalMM',
} as const satisfies MastraAgentRef;

export const SPROUT_AGENT = {
    agentKey: 'SproutAgent',
    id: 'sprout-agent',
    name: 'Sprout',
} as const satisfies MastraAgentRef;

export const WEATHER_AGENT = {
    agentKey: 'WeatherAgent',
    id: 'weather-agent',
    name: 'Weather Agent',
} as const satisfies MastraAgentRef;

export type AiAgentId =
    | typeof CONVERSATIONAL_MM_AGENT.id
    | typeof SPROUT_AGENT.id;

/** Member agents available in every environment. */
export const MEMBER_AGENTS: readonly MastraAgentRef[] = [SPROUT_AGENT];

/**
 * Extra member agents the app lists only when Sprout dev tools are on.
 * Register the matching Agent instance in `EXPERIMENTAL_MEMBER_AGENTS`.
 */
export const EXPERIMENTAL_MEMBER_AGENT_REFS: readonly MastraAgentRef[] = [];

const memberAgentRefs = (
    allowExperimentalAgents: boolean,
): readonly MastraAgentRef[] =>
    allowExperimentalAgents
        ? [...MEMBER_AGENTS, ...EXPERIMENTAL_MEMBER_AGENT_REFS]
        : MEMBER_AGENTS;

/** Unknown or disallowed keys become Sprout. */
export const resolveMemberAgent = ({
    agentKey,
    allowExperimentalAgents,
}: {
    agentKey: unknown;
    allowExperimentalAgents: boolean;
}): MastraAgentRef =>
    memberAgentRefs(allowExperimentalAgents).find(
        (agent) => agent.agentKey === agentKey,
    ) ?? SPROUT_AGENT;

/** Member-JWT agents on `/member/agents/:agentId/stream` */
export const isMemberAuthenticatedAgentKey = (agentKey: string): boolean =>
    memberAgentRefs(true).some((agent) => agent.agentKey === agentKey);

/**
 * Filtered member-stream agents that skip JWT (CORS is the browser gate)
 */
export const isPublicMemberAgentKey = (agentKey: string): boolean =>
    agentKey === CONVERSATIONAL_MM_AGENT.agentKey ||
    agentKey === WEATHER_AGENT.agentKey;

export const getMemberAgentIdForKey = (agentKey: string): string | null => {
    const memberAgent = memberAgentRefs(true).find(
        (agent) => agent.agentKey === agentKey,
    );
    if (memberAgent != null) {
        return memberAgent.id;
    }

    switch (agentKey) {
        case CONVERSATIONAL_MM_AGENT.agentKey:
            return CONVERSATIONAL_MM_AGENT.id;
        default:
            return null;
    }
};
